import * as React from 'react';
import {
    Alert,
    Button,
    Chip,
    IconButton,
    Link as MuiLink,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    getPrivateClubsByTemplate,
    getPrivateClubTemplates,
} from '../Api';
import PrivateClubAdminDto, {
    PrivateClubTemplateAdminDto,
} from '../models/dtos/PrivateClubAdminDto';
import CreatePrivateClubDialog from '../components/CreatePrivateClubDialog';

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatBetcoins(value: number) {
    return value.toLocaleString();
}

function feeBankLabel(club: PrivateClubAdminDto) {
    if (club.rewardMode !== 'BETCOIN') return 'Social';
    return `${formatBetcoins(club.entryFeeBetcoins ?? 0)} / ${formatBetcoins(club.bankBetcoins)}`;
}

function activeMembersCount(club: PrivateClubAdminDto) {
    return club.memberships.filter(member => member.status === 'ACTIVE').length;
}

function ClubLeaderCell({ club }: { club: PrivateClubAdminDto }) {
    const leader = [...club.leaderboard].sort((a, b) => a.rank - b.rank)[0] ?? null;
    if (!leader) return <TableCell>-</TableCell>;

    return (
        <TableCell>
            <Stack spacing={0.25} alignItems="flex-start">
                <MuiLink
                    href={`/users/${leader.userId}`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                >
                    {leader.user?.name?.trim() || 'Unknown user'}
                </MuiLink>
                <Typography variant="caption" color="text.secondary">
                    {leader.totalPoints} pts
                </Typography>
            </Stack>
        </TableCell>
    );
}

function ownerName(club: PrivateClubAdminDto) {
    return club.ownerUser?.name?.trim() || 'Unknown user';
}

function privateClubTemplateBetsUrl(templateId: string, clubId: string) {
    const params = new URLSearchParams({ clubIds: clubId });
    return `/private-clubs/templates/${encodeURIComponent(templateId)}/placed-bets?${params.toString()}`;
}

export default function PrivateClubTemplateClubsPage() {
    const navigate = useNavigate();
    const { templateId = '' } = useParams<{ templateId: string }>();
    const [template, setTemplate] = React.useState<PrivateClubTemplateAdminDto | null>(null);
    const [templates, setTemplates] = React.useState<PrivateClubTemplateAdminDto[]>([]);
    const [clubs, setClubs] = React.useState<PrivateClubAdminDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

    const load = React.useCallback(async () => {
        if (!templateId) {
            setError('Template id is required.');
            return;
        }
        setLoading(true);
        setError(null);
        const offset = page * rowsPerPage;
        const [clubsResponse, templatesResponse] = await Promise.all([
            getPrivateClubsByTemplate(templateId, offset, rowsPerPage),
            getPrivateClubTemplates(),
        ]);
        setClubs(clubsResponse?.items ?? []);
        setTotalItems(clubsResponse?.totalItems ?? 0);
        setTemplates(templatesResponse ?? []);
        setTemplate(templatesResponse?.find(item => item.id === templateId) ?? null);
        setLoading(false);
        if (!clubsResponse) setError('Failed to load private clubs for template.');
        if (!templatesResponse) setError(prev => prev ?? 'Failed to load private club template.');
    }, [page, rowsPerPage, templateId]);

    React.useEffect(() => {
        load();
    }, [load]);

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Stack gap={0.5}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/private-clubs')} sx={{ alignSelf: 'flex-start' }}>
                        Back to templates
                    </Button>
                    <Typography variant="h5">Private clubs</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Template: {template?.title ?? templateId}
                    </Typography>
                    {template && (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" label={template.status} color={template.status === 'ACTIVE' ? 'success' : 'default'} />
                            <Chip size="small" variant="outlined" label={`${template.clubsCount} clubs`} />
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setMessage(null);
                            setCreateDialogOpen(true);
                        }}
                        disabled={loading || !template || template.status !== 'ACTIVE'}
                    >
                        Create club
                    </Button>
                    <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Club</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Fee / Bank</TableCell>
                        <TableCell>Members</TableCell>
                        <TableCell>Picks</TableCell>
                        <TableCell>Leader</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Invite token</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={11} align="center">Loading private clubs...</TableCell>
                        </TableRow>
                    )}
                    {!loading && clubs.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={11} align="center">No private clubs found for this template</TableCell>
                        </TableRow>
                    )}
                    {!loading && clubs.map(club => (
                        <TableRow key={club.id}>
                            <TableCell>
                                <Stack spacing={0.25}>
                                    <MuiLink component={RouterLink} to={`/users/${club.createdBy}/private-clubs/${club.id}`} underline="hover">
                                        {club.name}
                                    </MuiLink>
                                    <Typography variant="caption" color="text.secondary">{club.id}</Typography>
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Chip size="small" label={club.status} color={club.status === 'ACTIVE' ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={club.rewardMode}
                                    color={club.rewardMode === 'BETCOIN' ? 'primary' : 'default'}
                                />
                            </TableCell>
                            <TableCell>{feeBankLabel(club)}</TableCell>
                            <TableCell>{activeMembersCount(club)} / {club.memberships.length}</TableCell>
                            <TableCell>
                                <MuiLink
                                    href={privateClubTemplateBetsUrl(templateId, club.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    underline="hover"
                                >
                                    {club.placedBetsCount}
                                </MuiLink>
                            </TableCell>
                            <ClubLeaderCell club={club} />
                            <TableCell>
                                <MuiLink href={`/users/${club.createdBy}`} target="_blank" rel="noreferrer" underline="hover">
                                    {ownerName(club)}
                                </MuiLink>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 180, overflowWrap: 'anywhere' }}>{club.inviteToken ?? '-'}</TableCell>
                            <TableCell>{formatDate(club.updatedAt)}</TableCell>
                            <TableCell align="right">
                                <Tooltip title="Open">
                                    <IconButton
                                        size="small"
                                        onClick={() => navigate(`/users/${club.createdBy}/private-clubs/${club.id}`)}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={event => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />

            <CreatePrivateClubDialog
                open={createDialogOpen}
                templates={templates}
                initialTemplateId={templateId}
                onClose={() => setCreateDialogOpen(false)}
                onCreated={async club => {
                    setMessage(`Private club "${club.name}" created for user ${club.createdBy}.`);
                    if (page === 0) await load();
                    else setPage(0);
                }}
            />
        </Stack>
    );
}
